import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ExternalLink, FlaskConical, Leaf, Phone, Sparkles, Package, ShieldCheck, Bandage, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import QuizDialog from "@/components/QuizDialog";

const PRODUCTS = [
  { img: "/assets/dermagen-balm.png", name: "Manuka Oil Balm", price: "From $11.95", note: "Suitable for babies 6 months+", url: "https://dermagen.net.au/products/manuka-oil-balm" },
  { img: "/assets/dermagen-lotion.png", name: "Manuka Oil Lotion", price: "From $36.95", note: "Everyday hydration, 3 years+", url: "https://dermagen.net.au/products/manuka-oil-lotion" },
  { img: "/assets/dermagen-serum.png", name: "Active 8 Serum", price: "From $14.95", note: "Soothes skin inflammation", url: "https://dermagen.net.au/products/active-8-serum-soothes-skin-inflammation" },
];

const LEARN_MODULES = [
  {
    icon: Package,
    title: "Learn about our DermaGen products",
    desc: "The full 2026 brand & product training deck — hero ingredients, the range, and who each product suits.",
    url: "https://dermagen-learn.emergent.host/deck/ba842e63-5733-4104-a270-58fb1bd8feab",
    testid: "learn-module-products",
  },
  {
    icon: ShieldCheck,
    title: "How to care for your skin barrier",
    desc: "What the skin barrier does, what damages it, and the daily habits that keep it strong.",
    url: "https://dermagen-learn.emergent.host/?utm_source=share",
    testid: "learn-module-barrier",
  },
  {
    icon: Bandage,
    title: "How to support wound healing",
    desc: "Minor wound care basics, and how nature-derived ingredients like Manuka Oil support healing.",
    url: "https://dermagen-learn.emergent.host/?utm_source=share",
    testid: "learn-module-wound",
  },
];

export default function DermaGenSection() {
  const [quizOpen, setQuizOpen] = useState(false);
  const [learnModules, setLearnModules] = useState(LEARN_MODULES);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/learn`)
      .then((r) => r.json())
      .then((d) => { if (d.items?.length) setLearnModules(d.items); })
      .catch(() => {});
  }, []);

  return (
    <section id="dermagen" data-testid="dermagen-section" className="scroll-mt-24 bg-[#F4F7F4] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <div className="grid items-start gap-12 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <img
              src="/assets/dermagen-logo.webp"
              alt="DermaGen by Botanical Chemist"
              data-testid="dermagen-logo"
              className="w-64 max-w-full rounded-xl"
            />
            <h2 className="mt-8 font-serif text-3xl italic leading-snug tracking-tight sm:text-4xl" data-testid="dermagen-heading">
              Born in our compounding room
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              DermaGen began right here in our pharmacy's compounding room, where pharmacist Karen Cheah blended
              pharmaceutical expertise with nature's most healing botanicals — hero ingredients
              <span className="font-semibold text-foreground"> Manuka Oil</span> and
              <span className="font-semibold text-foreground"> Bee Propolis</span>.
            </p>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              Steroid-free, ARTG-listed skincare for dry, itchy and sensitive skin — eczema, dermatitis, minor wounds
              and acne-prone skin. Now stocked in pharmacies Australia-wide, and it all started in Margaret River.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button
                asChild
                data-testid="dermagen-shop-btn"
                className="rounded-full bg-foreground px-6 font-heading font-semibold text-white hover:bg-black"
              >
                <a href="https://dermagen.net.au" target="_blank" rel="noopener noreferrer">
                  Shop DermaGen <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="dermagen-consult-btn"
                className="rounded-full border-2 border-foreground/20 px-6 font-heading font-semibold hover:border-brand-green hover:text-brand-greendark"
              >
                <a href="tel:+61897572224"><Phone className="mr-2 h-4 w-4" /> Ask our pharmacist</a>
              </Button>
            </div>
          </motion.div>

          <div className="lg:col-span-7">
            <motion.img
              src="/assets/dermagen-badges.webp"
              alt="DermaGen badges: ARTG listed, steroid free, 100% nature derived, pharmacist formulated, sustainably manufactured, made in Western Australia, not tested on animals"
              data-testid="dermagen-badges"
              initial={{ opacity: 0, scale: 0.97 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="w-full rounded-2xl bg-white p-4 shadow-[0_8px_32px_rgba(0,0,0,0.05)]"
            />
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3" data-testid="dermagen-products">
              {PRODUCTS.map((p, i) => (
                <motion.a
                  key={p.name}
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`dermagen-product-${i}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.08 }}
                  className="group rounded-2xl border border-border/60 bg-white p-5 shadow-[0_8px_24px_rgba(0,0,0,0.04)] transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[0_16px_36px_rgba(0,0,0,0.08)]"
                >
                  <div className="flex h-36 items-center justify-center overflow-hidden rounded-xl bg-[#F4F7F4]">
                    <img src={p.img} alt={p.name} className="h-full object-contain transition-transform duration-300 group-hover:scale-105" />
                  </div>
                  <h3 className="mt-4 font-heading text-sm font-bold">{p.name}</h3>
                  <p className="mt-1 text-xs text-muted-foreground">{p.note}</p>
                  <p className="mt-2 text-sm font-semibold text-brand-greendark">{p.price}</p>
                </motion.a>
              ))}
            </div>

            <div className="mt-12" data-testid="dermagen-learn">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-greendark" data-testid="learn-eyebrow">Learn with DermaGen</p>
              <h3 className="mt-3 font-serif text-2xl italic sm:text-3xl" data-testid="learn-heading">Free training modules, narrated slide by slide</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground md:text-base">
                From our DermaGen training portal — for patients, stockists and pharmacy staff alike.
              </p>
              <div className="mt-6 grid gap-4 sm:grid-cols-3" data-testid="learn-modules">
                {learnModules.map((m, i) => {
                  const Icon = m.icon || [Package, ShieldCheck, Bandage][i % 3];
                  return (
                  <a
                    key={m.id || m.title}
                    href={m.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={m.testid || `learn-module-${(m.id || m.title).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="group rounded-2xl border border-border/60 bg-white p-6 shadow-[0_8px_24px_rgba(0,0,0,0.04)] transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[0_14px_36px_rgba(0,0,0,0.08)]"
                  >
                    <div className="flex items-center justify-between">
                      <div className="inline-flex rounded-xl bg-brand-green/10 p-3 text-brand-greendark">
                        <Icon className="h-5 w-5" />
                      </div>
                      <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-[transform,color] duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-brand-greendark" />
                    </div>
                    <h4 className="mt-4 font-heading text-sm font-bold leading-snug">{m.title}</h4>
                    <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{m.desc}</p>
                  </a>
                  );
                })}
              </div>
              <div className="mt-5 text-center">
                <a
                  href="https://dermagen-learn.emergent.host/?utm_source=share"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="learn-portal-link"
                  className="inline-flex items-center gap-2 rounded-full border-2 border-brand-green/50 bg-white px-6 py-3 font-heading text-sm font-semibold text-brand-greendark transition-colors duration-200 hover:border-brand-green hover:bg-brand-green/10"
                >
                  Visit the full DermaGen training portal <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 grid gap-5 sm:grid-cols-2" data-testid="dermagen-pillars">
          <div className="flex items-start gap-4 rounded-2xl bg-white/70 p-6">
            <FlaskConical className="h-7 w-7 shrink-0 text-brand-pink" />
            <div>
              <h3 className="font-heading font-bold">Pharmacist formulated</h3>
              <p className="mt-1 text-sm text-muted-foreground">Every batch of Manuka Oil is third-party tested for active plant compounds.</p>
            </div>
          </div>
          <div className="flex items-start gap-4 rounded-2xl bg-white/70 p-6">
            <Leaf className="h-7 w-7 shrink-0 text-brand-green" />
            <div>
              <h3 className="font-heading font-bold">Nature-led, science-backed</h3>
              <p className="mt-1 text-sm text-muted-foreground">100% nature-derived ingredients, sustainably manufactured in Western Australia, never tested on animals.</p>
            </div>
          </div>
        </div>

        <div
          className="mt-10 flex flex-col items-start gap-5 rounded-2xl bg-foreground p-8 text-white sm:flex-row sm:items-center sm:justify-between"
          data-testid="dermagen-quiz-cta"
        >
          <div>
            <h3 className="flex items-center gap-2 font-serif text-2xl italic">
              <Sparkles className="h-5 w-5 text-brand-green" /> Find your perfect skincare match
            </h3>
            <p className="mt-2 text-sm text-white/70">
              Answer 3 quick questions and we'll point you to the right DermaGen product for your skin.
            </p>
          </div>
          <Button
            onClick={() => setQuizOpen(true)}
            data-testid="dermagen-quiz-open-btn"
            className="shrink-0 rounded-full bg-white px-6 font-heading font-semibold text-foreground hover:bg-brand-blush"
          >
            Take the 30-second quiz
          </Button>
        </div>
      </div>
      <QuizDialog open={quizOpen} onOpenChange={setQuizOpen} />
    </section>
  );
}
